/**
 * Session and project selection for the Control Center.
 *
 * The signed-in user and the selected project live here so every panel reads the
 * same source. The access token is in-memory only (see `api/client.ts`); a reload
 * restores the session from the HttpOnly refresh cookie via `POST /api/auth/restore`
 * (docs/plan/SESIJA-DIZAJN-2026-11-02.md). The session has an idle window (default
 * 5 min, chosen in Settings): while the user is active it is renewed every ~2 min, and
 * without activity it ends - locally here (drafts cleared, logout sent) and on the
 * server (the window simply expires).
 *
 * Server-side authorization is authoritative. The `permissions` map from
 * `/api/auth/me` is used only to hide controls the user cannot use; the API
 * re-checks every request, and a hidden button is not a security boundary.
 */

import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  api,
  ApiError,
  CurrentUser,
  hasAccessToken,
  Project,
  setAccessToken,
  StaleRestoreError,
} from "@/api/client";
import { clearChatStorage } from "./chatDraft";
import {
  clearActivity,
  IDLE_OPTIONS,
  lastActivity,
  loadIdleMinutes,
  markActivity,
  saveIdleMinutes,
} from "./sessionSettings";

const HEARTBEAT_MS = 60_000;
const RENEW_EVERY_MS = 120_000;
const AUTH_CHANNEL = "mb-auth";
const ACTIVITY_EVENTS = ["pointerdown", "keydown", "touchstart", "scroll", "focus"] as const;

const TAB_USER_KEY = "mb-tab-user";

/**
 * The account THIS tab last showed, kept in sessionStorage (per tab, survives a reload, never shared).
 * The refresh cookie is shared by every tab, so after another tab signed in as someone else a reload here
 * would otherwise silently become that other account (UI-SES-16).
 */
function tabUser(): { read: () => string | null; write: (id: string) => void; clear: () => void } {
  const store = (): Storage | null => {
    try {
      return typeof window !== "undefined" ? window.sessionStorage : null;
    } catch {
      return null;
    }
  };
  return {
    read: () => { try { return store()?.getItem(TAB_USER_KEY) ?? null; } catch { return null; } },
    write: (id) => { try { store()?.setItem(TAB_USER_KEY, id); } catch { /* ignore */ } },
    clear: () => { try { store()?.removeItem(TAB_USER_KEY); } catch { /* ignore */ } },
  };
}

const SIGNED_OUT_KEY = "mb-signed-out";

/**
 * Durable "this browser was explicitly signed out" fence (localStorage). Set BEFORE the logout request is sent,
 * so a logout that never reached the server cannot be undone by the next reload silently restoring the cookie
 * session (UI-SES-34). Only an explicit sign-in lifts it.
 */
function signedOutFence(): { set: () => void; clear: () => void; isSet: () => boolean } {
  const store = (): Storage | null => {
    try {
      return typeof window !== "undefined" ? window.localStorage : null;
    } catch {
      return null;
    }
  };
  return {
    set: () => { try { store()?.setItem(SIGNED_OUT_KEY, "1"); } catch { /* ignore */ } },
    clear: () => { try { store()?.removeItem(SIGNED_OUT_KEY); } catch { /* ignore */ } },
    isSet: () => { try { return store()?.getItem(SIGNED_OUT_KEY) === "1"; } catch { return false; } },
  };
}

function browserStorage(): Storage | null {
  try {
    return typeof window !== "undefined" ? window.localStorage : null;
  } catch {
    return null;
  }
}

function clearChatDrafts() {
  try {
    clearChatStorage(typeof window !== "undefined" ? window.localStorage : null);
  } catch {
    // ignore storage failures
  }
}

interface SessionState {
  user: CurrentUser | null;
  projects: Project[];
  project: Project | null;
  loading: boolean;
  error: string | null;
  idleMinutes: number;
  setIdleMinutes: (minutes: number) => Promise<void>;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  selectProject: (projectId: string) => void;
  reloadProjects: () => Promise<void>;
  can: (permission: string) => boolean;
}

const SessionContext = createContext<SessionState | null>(null);

export function SessionProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<CurrentUser | null>(null);
  const [projects, setProjects] = useState<Project[]>([]);
  const [projectId, setProjectId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [idleMinutes, setIdleMinutesState] = useState<number>(() => loadIdleMinutes(browserStorage()));
  const idleRef = useRef(idleMinutes);
  idleRef.current = idleMinutes;
  const userRef = useRef<CurrentUser | null>(null);
  userRef.current = user;
  const channelRef = useRef<{ postMessage: (message: unknown) => void } | null>(null);
  // While the mount restore (and the /me load that follows it) is pending, cross-tab announcements are
  // collected and compared with the account the restore returns (UI-SES-26, 28).
  const restoringRef = useRef(false);
  const signedOutDuringRestoreRef = useRef(false);
  const announcedDuringRestoreRef = useRef<Set<string>>(new Set());

  const announce = useCallback((message: { type: "signin"; userId: string } | { type: "signout" }) => {
    try {
      channelRef.current?.postMessage(message);
    } catch {
      // other tabs simply do not hear about it
    }
  }, []);

  /** Take the window the SERVER reports (a stale tab must adopt it, never overwrite it). */
  const adoptIdle = useCallback((minutes: unknown) => {
    if (typeof minutes === "number" && (IDLE_OPTIONS as readonly number[]).includes(minutes)) {
      setIdleMinutesState(minutes);
      // Written only when it differs from what this browser already holds, so the default
      // is never persisted as if the user had chosen it.
      if (loadIdleMinutes(browserStorage()) !== minutes) saveIdleMinutes(browserStorage(), minutes);
    }
  }, []);

  /** End the session on this tab only: no network call, nothing from it is kept. */
  const dropLocalSession = useCallback((keepTabAccount = false) => {
    setAccessToken(null);
    // A foreign-account drop keeps the tab's memory of its OWN account, so a reload cannot adopt the other
    // one (UI-SES-33); every other end of a session forgets it.
    if (!keepTabAccount) tabUser().clear();
    clearChatDrafts();
    clearActivity(browserStorage());
    setUser(null);
    setProjects([]);
    setProjectId(null);
  }, []);

  const loadSession = useCallback(async (expectedUserId?: string): Promise<CurrentUser | null> => {
    setLoading(true);
    setError(null);
    try {
      const me = await api.me();
      if (expectedUserId !== undefined && me.id !== expectedUserId) {
        // The cookie belongs to another account than the one this answer describes:
        // never adopt it.
        dropLocalSession(true);
        return null;
      }
      const list = await api.listProjects();
      tabUser().write(me.id);
      setUser(me);
      setProjects(list);
      setProjectId((current) =>
        current && list.some((p) => p.id === current)
          ? current
          : list.length > 0
            ? list[0].id
            : null,
      );
      return me;
    } catch (err) {
      setAccessToken(null);
      tabUser().clear();
      clearChatDrafts();
      setUser(null);
      setProjects([]);
      setProjectId(null);
      if (err instanceof ApiError && err.status !== 401) setError(err.messageHr);
      return null;
    } finally {
      setLoading(false);
    }
  }, [dropLocalSession]);

  // On mount: a token already in this tab (rare) is used as is; otherwise try to restore
  // the session from the cookie. Any refusal ends quietly at the sign-in screen.
  useEffect(() => {
    if (hasAccessToken()) {
      void loadSession();
      return;
    }
    if (signedOutFence().isSet()) {
      // Explicitly signed out in this browser: stay at the sign-in screen until someone signs in again.
      setLoading(false);
      return;
    }
    let cancelled = false;
    restoringRef.current = true;
    signedOutDuringRestoreRef.current = false;
    announcedDuringRestoreRef.current = new Set();
    const foreignAnnounced = (restoredId: string): boolean =>
      signedOutDuringRestoreRef.current || [...announcedDuringRestoreRef.current].some((id) => id !== restoredId);
    void (async () => {
      try {
        const result = await api.restore();
        if (cancelled) return;
        const remembered = tabUser().read();
        if (foreignAnnounced(result.user_id)) {
          // Another tab signed in as someone else (or signed out) while this restore was pending.
          dropLocalSession(true);
          setLoading(false);
          return;
        }
        if (remembered && remembered !== result.user_id) {
          // This tab showed another account; the shared cookie now belongs to someone else.
          dropLocalSession(true);
          setError("U ovom pregledniku je prijavljen drugi račun. Prijavite se ponovno.");
          setLoading(false);
          return;
        }
        adoptIdle(result.idle_minutes);
        await loadSession(result.user_id);
        if (!cancelled && foreignAnnounced(result.user_id)) dropLocalSession(true);
      } catch {
        if (!cancelled) {
          setAccessToken(null);
          setLoading(false);
        }
      } finally {
        restoringRef.current = false;
      }
    })();
    return () => {
      cancelled = true;
      restoringRef.current = false;
    };
  }, [loadSession, adoptIdle, dropLocalSession]);

  // Other tabs: a different account signing in, or a sign-out, ends this tab's session too.
  useEffect(() => {
    if (typeof BroadcastChannel === "undefined") return;
    let channel: BroadcastChannel;
    try {
      channel = new BroadcastChannel(AUTH_CHANNEL);
    } catch {
      return;
    }
    channelRef.current = channel;
    channel.onmessage = (event: MessageEvent) => {
      const message = event.data as { type?: string; userId?: string } | null;
      const current = userRef.current;
      if (!message) return;
      if (!current) {
        // No user yet. If the mount restore is pending, a sign-out, or a sign-in of an account other than
        // the one this tab showed, makes its answer untrustworthy (UI-SES-26).
        if (restoringRef.current) {
          if (message.type === "signout") signedOutDuringRestoreRef.current = true;
          else if (message.type === "signin" && typeof message.userId === "string") announcedDuringRestoreRef.current.add(message.userId);
        }
        return;
      }
      if (message.type === "signout") dropLocalSession();
      else if (message.type === "signin" && message.userId !== current.id) dropLocalSession(true);
    };
    return () => {
      channelRef.current = null;
      channel.close();
    };
  }, [dropLocalSession]);

  const signIn = useCallback(
    async (email: string, password: string) => {
      setLoading(true);
      setError(null);
      try {
        await api.login(email, password, idleRef.current);
        signedOutFence().clear();
        const me = await loadSession();
        if (me) announce({ type: "signin", userId: me.id });
      } catch (err) {
        setAccessToken(null);
        // The message is kept in the **provider**, not in `LoginPage`. `loading`
        // switches the gate to its spinner, which unmounts the login form; a message
        // held in that component's own state is written after it is already gone and
        // the user never sees why the sign-in failed. This is a real defect the
        // browser test caught.
        setError(
          err instanceof ApiError
            ? err.status === 401
              ? "Neispravan email ili lozinka."
              : err.messageHr
            : "Prijava nije uspjela.",
        );
        setLoading(false);
        throw err;
      }
    },
    [loadSession, announce],
  );

  const signOut = useCallback(async () => {
    // Local removal must not depend on the network: clear drafts and the signed-in
    // UI state immediately, before the logout request is even attempted.
    signedOutFence().set();
    clearChatDrafts();
    clearActivity(browserStorage());
    tabUser().clear();
    setUser(null);
    setProjects([]);
    setProjectId(null);
    announce({ type: "signout" });
    try {
      // `api.logout` captures the token synchronously for the request and then drops it
      // locally at once, so the credential is gone before the call settles. It is not
      // cleared again here: a late completion must not wipe the token of a session
      // started in the meantime.
      await api.logout();
    } catch {
      // A failed logout still clears local state; the refresh cookie expires on its
      // own and the session is revoked server-side when the call succeeds.
    }
  }, [announce]);

  // Idle window + keep-alive, only while someone is signed in.
  const signedInUserId = user?.id ?? null;
  useEffect(() => {
    if (!signedInUserId) return;
    const store = browserStorage();
    const signedInAt = Date.now();
    let lastRenewAt = signedInAt;
    let activeSinceRenew = false;
    let renewing = false;
    let stopped = false;

    const onActivity = () => {
      markActivity(store, signedInUserId, Date.now());
      activeSinceRenew = true;
    };

    const renew = async () => {
      renewing = true;
      try {
        const result = await api.restore();
        if (stopped) return;
        if (result.user_id !== signedInUserId) {
          dropLocalSession(true);
          return;
        }
        adoptIdle(result.idle_minutes);
        lastRenewAt = Date.now();
        activeSinceRenew = false;
      } catch (err) {
        if (err instanceof StaleRestoreError) return; // this tab already dropped/replaced the session
        // 401 = the session is gone; anything else (network, 5xx) is repeated on a later tick:
        // restore does not rotate anything, so repeating it is harmless.
        if (!stopped && err instanceof ApiError && err.status === 401) dropLocalSession();
      } finally {
        renewing = false;
      }
    };

    const tick = () => {
      if (stopped) return;
      const now = Date.now();
      const last = Math.max(signedInAt, lastActivity(store, signedInUserId, now) ?? 0);
      if (now - last >= idleRef.current * 60_000) {
        stopped = true; // the sign-out removes the user, but React tears this effect down only after it commits
        void signOut();
        return;
      }
      if (activeSinceRenew && !renewing && now - lastRenewAt >= RENEW_EVERY_MS) void renew();
    };

    const onVisibility = () => {
      if (document.visibilityState === "visible") tick();
    };

    ACTIVITY_EVENTS.forEach((name) => document.addEventListener(name, onActivity, { capture: true, passive: true }));
    document.addEventListener("visibilitychange", onVisibility);
    const timer = setInterval(tick, HEARTBEAT_MS);
    return () => {
      stopped = true;
      clearInterval(timer);
      ACTIVITY_EVENTS.forEach((name) => document.removeEventListener(name, onActivity, { capture: true } as EventListenerOptions));
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [signedInUserId, signOut, dropLocalSession, adoptIdle]);

  const setIdleMinutes = useCallback(
    async (minutes: number) => {
      if (!(IDLE_OPTIONS as readonly number[]).includes(minutes)) return;
      const current = userRef.current;
      if (!current) {
        saveIdleMinutes(browserStorage(), minutes);
        setIdleMinutesState(minutes);
        return;
      }
      try {
        // Saved only after the server accepted it: a refused or unconfirmed change must not
        // look like it worked.
        const result = await api.restore(minutes);
        if (result.user_id !== current.id) {
          dropLocalSession(true);
          return;
        }
        saveIdleMinutes(browserStorage(), minutes);
        setIdleMinutesState(minutes);
      } catch (err) {
        if (err instanceof ApiError && err.status === 401) dropLocalSession();
      }
    },
    [dropLocalSession],
  );

  const selectProject = useCallback((id: string) => setProjectId(id), []);

  const can = useCallback(
    (permission: string) => {
      if (!user) return false;
      if (!projectId) return false;
      const granted = user.permissions[projectId];
      return Array.isArray(granted) && granted.includes(permission);
    },
    [user, projectId],
  );

  const project = useMemo(
    () => projects.find((p) => p.id === projectId) ?? null,
    [projects, projectId],
  );

  const value = useMemo<SessionState>(
    () => ({
      user,
      projects,
      project,
      loading,
      error,
      idleMinutes,
      setIdleMinutes,
      signIn,
      signOut,
      selectProject,
      reloadProjects: async () => { await loadSession(); },
      can,
    }),
    [user, projects, project, loading, error, idleMinutes, setIdleMinutes, signIn, signOut, selectProject, loadSession, can],
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionState {
  const context = useContext(SessionContext);
  if (!context) throw new Error("useSession must be used inside SessionProvider");
  return context;
}

/** Project-level permission names the UI checks before showing a control. */
export const PERMISSION = {
  jobStart: "job.start",
  jobCancel: "job.cancel",
  jobCancelOwn: "job.cancel_own",
  approvalView: "approval.view",
  approvalMedium: "approval.medium",
  approvalHigh: "approval.high",
  budgetView: "budget.view",
  auditView: "audit.view",
  killSwitchProject: "kill_switch.project",
} as const;
